/**
 * RS Customizer — Bead Mine Kids Multi
 * SVG path-based bead renderer with bidirectional fill.
 * Letters centered on path; blank beads fill the remaining arc in both directions.
 * Requires window.RSBeadPath (rs-bead-path-utils.js).
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is managed by rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const METAOBJECT_TYPE = 'customizer_bead_mine_kids_set';
  const LIQUID_VIEW = 'metaobject-bead-mine-kids-multi';
  const BEAD_MINE_KIDS_SHADOW = { distance: 8, opacity: 0.2, blur: 1.5 };

  let _injectedSlideId = null;
  let _baseConfig = null;
  let _letters = {};
  let _backgroundsData = null;
  let _currentVariantTitle = null;
  let _beadConfig = null;
  let _resizeTimer = null;

  const CONFIG = {
    svgContainerClass: 'bead-path-svg-container',
    beadContainerClass: 'bead-path-bead-container',
    letterBeadClass: 'bead-path-letter-bead',
    spacerBeadClass: 'bead-path-spacer-bead',
    blankBeadClass: 'bead-path-blank-bead',
  };

  // ─── Helpers ────────────────────────────────────────────────────

  function _getMtoText() {
    const fields = document.querySelectorAll('[data-mto-field]');
    let text = '';
    fields.forEach(function (field) { text += field.value; });
    return text;
  }

  function _getBeadsArrayLength(beadConfig) {
    const beads = (beadConfig || {}).beads;
    return Array.isArray(beads) && beads.length > 0 ? beads.length : 0;
  }

  function _getOutsideBlankBeadIndex(i, beadCount, beadsLength) {
    if (!beadsLength) return i;
    return beadsLength - 1 - ((beadCount - 1 - i) % beadsLength);
  }

  function _normalizeTitle(title) {
    return (title || '').replace(/[''']/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  // ─── Data fetching ──────────────────────────────────────────────

  function _fetchBaseConfig() {
    return fetch(window.RSProduct.config.productUrl + '?view=' + LIQUID_VIEW)
      .then(function (response) { return response.json(); });
  }

  // Query the specific metaobject by handle (= product handle) and extract
  // letters from its `letters` references field — same pattern as knotty/cord.
  const _LETTERS_QUERY = `
    query getBeadMineKidsMultiLetters($handle: MetaobjectHandleInput!) {
      metaobject(handle: $handle) {
        handle
        fields {
          key
          value
          references(first: 250) {
            edges {
              node {
                ... on Metaobject {
                  fields {
                    key
                    value
                    reference {
                      ... on MediaImage {
                        image { url width height }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  function _fetchLetters() {
    if (!window.RSGraphQL || !window.RSGraphQL.executeQuery) {
      return Promise.resolve({});
    }
    const productHandle = _baseConfig && _baseConfig.product_handle;
    if (!productHandle) {
      return Promise.resolve({});
    }
    return window.RSGraphQL.executeQuery(_LETTERS_QUERY, {
      handle: { handle: productHandle, type: METAOBJECT_TYPE },
    }).then(function (data) {
      if (!data || !data.metaobject) {
        return {};
      }
      const lettersField = data.metaobject.fields.find(function (field) { return field.key === 'letters'; });
      if (!lettersField || !lettersField.references || !lettersField.references.edges) {
        return {};
      }
      const result = {};
      lettersField.references.edges.forEach(function (edge) {
        const node = edge.node;
        if (!node || !node.fields) return;
        const entry = { key: null, image: null, left_recess: 0, right_recess: 0 };
        node.fields.forEach(function (field) {
          if (field.key === 'character' || field.key === 'key') {
            if (!entry.key) entry.key = field.value;
          } else if (field.key === 'image' && field.reference && field.reference.image) {
            entry.image = field.reference.image;
          } else if (field.key === 'left_recess') {
            entry.left_recess = parseFloat(field.value) || 0;
          } else if (field.key === 'right_recess') {
            entry.right_recess = parseFloat(field.value) || 0;
          }
        });
        if (!entry.key || !entry.image) return;
        result[entry.key] = {
          url: entry.image.url,
          width: entry.image.width || 150,
          height: entry.image.height || 150,
          aspectRatio: (entry.image.width && entry.image.height)
            ? entry.image.width / entry.image.height : 1,
          left_recess: entry.left_recess,
          right_recess: entry.right_recess,
        };
      });
      return result;
    }).catch(function (err) {
      console.error('[BeadMineKidsMulti] fetchLetters error:', err);
      return {};
    });
  }

  // ─── Background ─────────────────────────────────────────────────

  function _getBackgroundUrl() {
    if (!_backgroundsData) return null;
    if (_backgroundsData['default']) return _backgroundsData['default'];
    const key = Object.keys(_backgroundsData).find(function (key) { return _backgroundsData[key]; });
    return key ? _backgroundsData[key] : null;
  }

  function _getThumbnailUrl() {
    const thumbs = (_baseConfig && _baseConfig.thumbnails) || {};
    if (thumbs['default']) return thumbs['default'];
    const key = Object.keys(thumbs).find(function (key) { return thumbs[key]; });
    return key ? thumbs[key] : null;
  }

  function _getBackgroundForVariant(variantTitle) {
    if (!_backgroundsData || !variantTitle) return _getBackgroundUrl();
    const normalizedTitle = variantTitle.toLowerCase();
    let match = null;
    Object.entries(_backgroundsData).forEach(function (pair) {
      if (pair[0].toLowerCase() === normalizedTitle || normalizedTitle.includes(pair[0].toLowerCase())) match = pair[1];
    });
    return match || _getBackgroundUrl();
  }

  // Override the gallery thumbnail image with the dedicated thumbnail URL from the metaobject.
  // gallery.inject creates a thumbnail from the main background image; the metaobject provides
  // a separate smaller thumbnail image specifically designed for the gallery strip.
  function _applyThumbnailImage() {
    const thumbUrl = _getThumbnailUrl();
    if (!thumbUrl || !_injectedSlideId) return;
    // Fall back to querying any thumb that references this slide id in the thumb strip.
    const allThumbs = document.querySelectorAll('.product__thumb img, .splide__slide--thumbnail img');
    // Find the injected one — it's the one whose src matches the background URL.
    const bgUrl = _getBackgroundUrl();
    if (bgUrl) {
      allThumbs.forEach(function (img) {
        if (img.src && img.src.indexOf(bgUrl.split('?')[0]) !== -1) {
          img.src = thumbUrl;
        }
      });
    }
  }

  // ─── Bead config ────────────────────────────────────────────────

  function _buildBeadConfig() {
    if (!_baseConfig || !_baseConfig.settings) return null;
    const settings = _baseConfig.settings;
    return {
      svg_guide_content: settings.svg_guide_content,
      svg_view_box_width: settings.svg_view_box_width || 1110,
      svg_view_box_height: settings.svg_view_box_height || 880,
      bead_height: settings.bead_height || 90,
      letter_height: settings.letter_height || 90,
      bead_width: settings.bead_width || null,
      letter_width: settings.letter_width || null,
      letter_spacing: settings.letter_spacing || 0,
      bead_spacing: settings.bead_spacing || 0,
      y_offset: settings.y_offset || 0,
      random_offset: settings.random_offset === true || settings.random_offset === 'true',
      offset_factor: settings.offset_factor || 0,
      light_source_angle: settings.light_source_angle || 0,
      stroke_width: settings.stroke_width || 0,
      stroke_color: settings.stroke_color || 'rgba(0,0,0,0)',
      bead_rotation_offset: 0,
      beads: settings.beads || [],
      spacer: settings.spacer || {},
      letters: _letters,
    };
  }

  // ─── Canvas ─────────────────────────────────────────────────────

  function _getCustomizerView() {
    if (!_injectedSlideId) return null;
    const slide = document.querySelector('[data-rs-injected="' + _injectedSlideId + '"]');
    if (!slide) return null;
    return slide.querySelector('.customizer-view');
  }

  // ─── Bidirectional fill helpers ─────────────────────────────────

  function _renderLeftFill(bc, beadContainer, pathElement, occupied, scale, pathLength, scaledPathLength, hasLetters) {
    const BP = window.RSBeadPath;
    const beadsArrayLength = _getBeadsArrayLength(bc);
    const dims = BP.calculateBeadDimensions(bc);
    const spacing = dims.width * scale * 0.95;
    const sorted = occupied.slice().sort(function (a, b) { return a.position - b.position; });
    if (!sorted.length) return;
    const first = sorted[0];
    const firstStart = first.start !== undefined ? first.start : first.position - (first.size / 2 / pathLength);
    if (firstStart <= 0) return;
    const segLen = firstStart * scaledPathLength;
    const cnt = Math.min(500, Math.floor(segLen / spacing));
    if (cnt <= 0) return;
    const sp = segLen / cnt;
    for (var i = 0; i < cnt; i++) {
      const off = ((cnt - i - 0.5) * sp) / scaledPathLength;
      const pos = hasLetters ? firstStart - off : ((i + 0.5) * sp) / scaledPathLength;
      const idx = hasLetters ? _getOutsideBlankBeadIndex(i, cnt, beadsArrayLength) : i;
      BP.renderBlankBead(beadContainer, pathElement, pos * pathLength, bc, CONFIG.blankBeadClass, idx);
    }
  }

  function _renderRightFill(bc, beadContainer, pathElement, occupied, scale, pathLength, scaledPathLength, hasLetters) {
    const BP = window.RSBeadPath;
    const beadsArrayLength = _getBeadsArrayLength(bc);
    const dims = BP.calculateBeadDimensions(bc);
    const spacing = dims.width * scale * 0.95;
    const sorted = occupied.slice().sort(function (a, b) { return a.position - b.position; });
    if (!sorted.length) return;
    const last = sorted[sorted.length - 1];
    const lastEnd = last.end !== undefined ? last.end : last.position + (last.size / 2 / pathLength);
    if (lastEnd >= 1) return;
    const segStart = lastEnd;
    const segLen = (1 - segStart) * scaledPathLength;
    const cnt = Math.min(500, Math.floor(segLen / spacing));
    if (cnt <= 0) return;
    const sp = segLen / cnt;
    for (var i = 0; i < cnt; i++) {
      const rel = ((cnt - i - 0.5) * sp) / scaledPathLength;
      const pos = segStart + rel;
      const idx = hasLetters ? _getOutsideBlankBeadIndex(i, cnt, beadsArrayLength) : i;
      BP.renderBlankBead(beadContainer, pathElement, pos * pathLength, bc, CONFIG.blankBeadClass, idx);
    }
  }

  // ─── Main render ─────────────────────────────────────────────────

  function _renderBeadsWithText(container, text, bc) {
    const BP = window.RSBeadPath;
    if (!BP || !container || !bc) return;

    const svg = BP.createSVGContainer(container, bc, CONFIG.svgContainerClass);
    const pathEl = BP.createSVGPath(svg, bc.svg_guide_content);
    if (!pathEl) return;

    const beadCont = BP.createBeadContainer(container, CONFIG.beadContainerClass);
    BP.clearBeadsInContainer(beadCont, [CONFIG.letterBeadClass, CONFIG.spacerBeadClass, CONFIG.blankBeadClass]);

    const scale = BP.getResponsiveScale(container, bc);
    const pathLen = pathEl.getTotalLength();
    const scaledLen = pathLen * scale;
    const dims = BP.calculateBeadDimensions(bc);
    const beadSpacing = dims.width * scale * 0.95;

    let validLetters = [];
    if (text && text.trim()) {
      validLetters = text.toUpperCase().split('').filter(function (c) { return _letters[c]; });
    }

    // No valid letters → bidirectional blank fill
    if (!validLetters.length) {
      const cnt = Math.floor(scaledLen / beadSpacing);
      const totalW = cnt * beadSpacing;
      const startOff = (scaledLen - totalW) / 2;
      const cIdx = Math.floor(cnt / 2);
      for (var i = 0; i < cnt; i++) {
        const relPos = (startOff + (i + 0.5) * beadSpacing) / scaledLen;
        const idx = i < cIdx ? i : cnt - 1 - i;
        BP.renderBlankBead(beadCont, pathEl, relPos * pathLen, bc, CONFIG.blankBeadClass, idx);
      }
      BP.applyDropShadowToContainer(container, Object.assign({}, bc, { shadow: BEAD_MINE_KIDS_SHADOW }));
      return;
    }

    const renderer = BP.getRenderer(bc);
    const letterDims = validLetters.map(function (l) {
      const ld = _letters[l];
      if (ld) {
        const d = renderer.calculateLetterDimensions(ld, bc);
        return { width: d.width, height: d.height, left_recess: ld.left_recess || 0, right_recess: ld.right_recess || 0 };
      }
      return { width: (bc.bead_width || 80) * 1.2, height: bc.letter_height || (bc.bead_width || 80) * 1.2, left_recess: 0, right_recess: 0 };
    });

    const cp = 0.5;
    const totLW = letterDims.reduce(function (sum, d) { return sum + d.width; }, 0);
    const totRW = letterDims.reduce(function (sum, d) { return sum + d.left_recess + d.right_recess; }, 0);
    const spDims = BP.calculateBeadDimensions(bc);
    const spBW = spDims.width;
    const spPerGap = 2;
    const totSpW = (validLetters.length - 1) * spPerGap * spBW;
    const bSpc = bc.bead_spacing || 0;
    const totBSAdj = bSpc * (validLetters.length - 1) * (spPerGap - 1);
    let totCW = totLW + totSpW + totRW + totBSAdj;
    const lSpVal = (totCW * (bc.letter_spacing || 0)) / 100;
    totCW += lSpVal;
    const finCW = totCW * scale;
    const lGap = validLetters.length > 1 ? lSpVal / (validLetters.length - 1) : 0;

    // Compute occupied positions
    const occ = [];
    let tOff = -(finCW / 2);
    for (var i = 0; i < validLetters.length; i++) {
      const ld = letterDims[i];
      tOff += ld.left_recess * scale;
      const lCO = tOff + ld.width * scale / 2;
      const lPos = Math.max(0, Math.min(1, cp + lCO / scaledLen));
      const occS = tOff - ld.left_recess * scale;
      const occE = tOff + ld.width * scale + ld.right_recess * scale;
      tOff += ld.width * scale + ld.right_recess * scale;
      occ.push({ position: lPos, type: 'letter',
        start: Math.max(0, Math.min(1, cp + occS / scaledLen)),
        end: Math.max(0, Math.min(1, cp + occE / scaledLen)),
        size: Math.max(ld.width, ld.height) });
      if (i < validLetters.length - 1) {
        tOff += lGap * scale;
        for (var bi = 0; bi < spPerGap; bi++) {
          const bCO = tOff + spBW * scale / 2;
          const bPos = Math.max(0, Math.min(1, cp + bCO / scaledLen));
          occ.push({ position: bPos, type: 'spacer',
            start: Math.max(0, Math.min(1, cp + tOff / scaledLen)),
            end: Math.max(0, Math.min(1, cp + (tOff + spBW * scale) / scaledLen)),
            size: spBW });
          tOff += spBW * scale;
          if (bi === 0) tOff += bSpc * scale;
        }
      }
    }

    // Left fill
    _renderLeftFill(bc, beadCont, pathEl, occ, scale, pathLen, scaledLen, true);

    // Between-letter spacers
    let cOff = -(finCW / 2);
    let bIdx = 0;
    for (var i = 0; i < validLetters.length; i++) {
      const ld = letterDims[i];
      cOff += ld.left_recess * scale + ld.width * scale + ld.right_recess * scale;
      if (i < validLetters.length - 1) {
        cOff += lGap * scale;
        for (var bi = 0; bi < spPerGap; bi++) {
          const bCO = cOff + spBW * scale / 2;
          const bPos = Math.max(0, Math.min(1, cp + bCO / scaledLen));
          const aIdx = ((bIdx * 2) + bi) % 12;
          BP.renderBlankBead(beadCont, pathEl, bPos * pathLen, bc, CONFIG.spacerBeadClass, aIdx);
          cOff += spBW * scale;
          if (bi === 0) cOff += bSpc * scale;
        }
        bIdx++;
      }
    }

    // Right fill
    _renderRightFill(bc, beadCont, pathEl, occ, scale, pathLen, scaledLen, true);

    // Letter beads (on top)
    cOff = -(finCW / 2);
    for (var i = 0; i < validLetters.length; i++) {
      const ld = letterDims[i];
      cOff += ld.left_recess * scale;
      const lCO = cOff + ld.width * scale / 2;
      const lPos = Math.max(0, Math.min(1, cp + lCO / scaledLen));
      BP.renderLetterBead(beadCont, pathEl, validLetters[i], lPos * pathLen, bc, _letters, CONFIG.letterBeadClass);
      cOff += ld.width * scale + ld.right_recess * scale;
      if (i < validLetters.length - 1) {
        cOff += lGap * scale;
        for (var bi = 0; bi < spPerGap; bi++) { cOff += spBW * scale; if (bi === 0) cOff += bSpc * scale; }
      }
    }

    BP.applyDropShadowToContainer(container, Object.assign({}, bc, { shadow: BEAD_MINE_KIDS_SHADOW }));
  }

  // ─── Event handlers ─────────────────────────────────────────────

  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    const container = _getCustomizerView();
    if (container && _beadConfig) _renderBeadsWithText(container, _getMtoText(), _beadConfig);
    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) return;
    const variant = event.data.variant;
    if (!variant || !variant.title) return;
    _currentVariantTitle = variant.title;
    if (_injectedSlideId) { window.RSProduct.gallery.remove(_injectedSlideId); _injectedSlideId = null; }
    const bgUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (bgUrl) _injectedSlideId = window.RSProduct.gallery.inject(bgUrl, 1);
    const container = _getCustomizerView();
    if (container && _beadConfig) _renderBeadsWithText(container, _getMtoText(), _beadConfig);
    if (_injectedSlideId) window.RSProduct.gallery.navigate(_injectedSlideId);
  }

  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(function () {
      const container = _getCustomizerView();
      if (container && _beadConfig) _renderBeadsWithText(container, _getMtoText(), _beadConfig);
    }, 250);
  }

  // ─── Init ───────────────────────────────────────────────────────

  function _init() {
    if (!window.RSProduct || !window.RSProduct.config.productHandle) return;
    if (!window.RSBeadPath) { console.error('[BeadMineKidsMulti] RSBeadPath not loaded'); return; }

    _fetchBaseConfig().then(function (baseConfig) {
      _baseConfig = baseConfig;
      _backgroundsData = baseConfig.backgrounds || {};
      const initVarId = String(window.RSProduct.config.initialVariantId || '');
      const variantNames = baseConfig.variant_names || {};
      const fullName = variantNames[initVarId] || '';
      const parts = fullName.split(' - ');
      _currentVariantTitle = parts.length > 1 ? parts[parts.length - 1] : fullName;
      const bgUrl = _getBackgroundForVariant(_currentVariantTitle);
      if (bgUrl) {
        _injectedSlideId = window.RSProduct.gallery.inject(bgUrl, 1);
        // Defer thumbnail override until gallery has rendered the thumb element.
        setTimeout(_applyThumbnailImage, 300);
      }
      if (baseConfig.settings) {
        document.documentElement.style.setProperty('--customizer-path-stroke-width', (baseConfig.settings.stroke_width || 0) + 'px');
        document.documentElement.style.setProperty('--customizer-path-stroke-color', baseConfig.settings.stroke_color || 'rgba(0,0,0,0)');
      }
      return _fetchLetters();
    }).then(function (letters) {
      _letters = letters || {};
      _beadConfig = _buildBeadConfig();
      const container = _getCustomizerView();
      if (container && _beadConfig) _renderBeadsWithText(container, _getMtoText(), _beadConfig);
      document.addEventListener('input', _handleInput);
      document.addEventListener('focusin', _handleInputFocus);
      window.addEventListener('resize', _onResize);
      if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
        window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, _handleVariantChange);
      }
    }).catch(function (e) { console.error('[BeadMineKidsMulti] Init error:', e); });
  }

  document.addEventListener('DOMContentLoaded', function () { _init(); });
})();
